"use client";

import { useMemo, useState } from "react";
import { EllipsisIcon, FolderPlusIcon, SquareMinusIcon, SquarePlusIcon, PencilIcon, Trash2Icon } from "lucide-react";
import { useTranslations } from "next-intl";

import {
  AdminTableIconActionButton,
} from "@/components/admin/admin-table-icon-actions";
import type { AttachGroupRecord } from "@/components/admin/attach-picker/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { HoverTip } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { orderAttachGroups } from "@/lib/attach/group-tree";

const SHARE_BADGE = {
  org: { badge: "orgBadge", title: "orgShareGroup" },
  division: { badge: "divisionBadge", title: "divisionShareGroup" },
} as const;

function GroupItemRow({
  group,
  isActive,
  disabled,
  expanded,
  onToggleExpand,
  editable = true,
  onSelect,
  onEdit,
  onDelete,
}: {
  group: AttachGroupRecord;
  isActive: boolean;
  disabled?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  editable?: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete?: () => void;
}) {
  const t = useTranslations("site.attach");
  const tUi = useTranslations("site.ui");
  const [actionsOpen, setActionsOpen] = useState(false);
  const share =
    group.scope === "org" || group.scope === "division"
      ? SHARE_BADGE[group.scope]
      : null;

  return (
    <div className="group/item relative">
      <button
        type="button"
        disabled={disabled}
        onClick={onSelect}
        className={cn(
          "relative w-full cursor-pointer py-1.5 pr-8 pl-2 text-left text-sm transition-colors disabled:cursor-not-allowed",
          isActive
            ? "bg-primary/10 text-primary"
            : "text-foreground/80 hover:bg-muted/60 hover:text-foreground",
        )}
      >
        <span className="flex min-w-0 items-center gap-1 pl-4">
          {share ? (
            <HoverTip content={t(share.title)}><span
              className="flex size-4 shrink-0 items-center justify-center rounded-sm bg-primary/12 text-[10px] leading-none text-primary"
            >
              {t(share.badge)}
            </span></HoverTip>
          ) : null}
          <span className="truncate">{group.name}</span>
        </span>
        <span className={cn("absolute top-1/2 right-2 -translate-y-1/2 text-xs tabular-nums text-muted-foreground", editable && "group-hover/item:invisible group-focus-within/item:invisible", actionsOpen && "invisible")}>
          {group.count}
        </span>
      </button>

      {onToggleExpand ? (
        <button
          type="button"
          disabled={disabled}
          aria-label={t(expanded ? "collapseGroup" : "expandGroup", { name: group.name })}
          aria-expanded={expanded}
          aria-controls={`attach-group-children-${group.id}`}
          className="absolute inset-y-0 left-0 flex w-6 cursor-pointer items-center justify-center text-muted-foreground hover:text-foreground disabled:cursor-not-allowed"
          onClick={onToggleExpand}
        >
          {expanded ? <SquareMinusIcon className="size-3.5" /> : <SquarePlusIcon className="size-3.5" />}
        </button>
      ) : null}

      {editable ? (
        <div className={cn("absolute inset-y-0 right-1 flex items-center opacity-0 transition-opacity group-hover/item:opacity-100 group-focus-within/item:opacity-100", actionsOpen && "opacity-100")}>
          <DropdownMenu onOpenChange={setActionsOpen}>
            <AdminTableIconActionButton
              label={`${group.name} · ${tUi("actions")}`}
              icon={EllipsisIcon}
              className="size-6"
              render={<DropdownMenuTrigger disabled={disabled} openOnHover />}
            />
            <DropdownMenuContent align="end" className="w-max min-w-0">
              <DropdownMenuItem disabled={disabled} onClick={onEdit}><PencilIcon />{t("edit")}</DropdownMenuItem>
              {onDelete ? <DropdownMenuItem disabled={disabled} variant="destructive" onClick={onDelete}><Trash2Icon />{t("delete")}</DropdownMenuItem> : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
    </div>
  );
}

export function AttachGroupSidebar({
  groups,
  activeGroupId,
  onSelectGroup,
  onRequestCreateGroup,
  onRequestRenameGroup,
  onDeleteGroup,
  expandedGroups,
  onToggleExpand,
  disabled,
}: {
  groups: AttachGroupRecord[];
  activeGroupId: string;
  onSelectGroup: (groupId: string) => void;

  onRequestCreateGroup: () => void;
  onRequestRenameGroup: (group: AttachGroupRecord) => void;
  onDeleteGroup: (groupId: string) => void;
  expandedGroups: Record<string, boolean>;
  onToggleExpand: (groupId: string, expanded: boolean) => void;
  disabled?: boolean;
}) {
  const t = useTranslations("site.attach");
  const orderedGroups = useMemo(() => orderAttachGroups(groups), [groups]);
  const activeParentId = groups.find(group => group.id === activeGroupId)?.parentId;
  const childrenById = useMemo(() => {
    const result = new Map<string, AttachGroupRecord[]>();
    for (const group of orderedGroups) {
      if (group.parentId) {
        const children = result.get(group.parentId) ?? [];
        children.push(group);
        result.set(group.parentId, children);
      }
    }
    return result;
  }, [orderedGroups]);

  function row(group: AttachGroupRecord, expanded?: boolean, toggle?: () => void) {
    return <GroupItemRow
      group={group}
      isActive={activeGroupId === group.id}
      disabled={disabled}
      editable={group.renamable}
      expanded={expanded}
      onToggleExpand={toggle}
      onSelect={() => onSelectGroup(group.id)}
      onEdit={() => onRequestRenameGroup(group)}
      onDelete={group.deletable ? () => onDeleteGroup(group.id) : undefined}
    />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col border-r bg-muted/20">
      <div className="shrink-0 border-b px-2 py-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-full justify-start"
          disabled={disabled}
          onClick={onRequestCreateGroup}
        >
          <FolderPlusIcon />
          {t("addGroup")}
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto py-1">
        {orderedGroups.filter(group => !group.parentId).map(group => {
          const children = childrenById.get(group.id) ?? [];
          const expanded = expandedGroups[group.id] ?? activeParentId === group.id;
          return <div key={group.id}>
            {row(group, expanded, children.length ? () => onToggleExpand(group.id, !expanded) : undefined)}
            {children.length ? <div id={`attach-group-children-${group.id}`} hidden={!expanded} className="pl-2">
              {children.map(child => <div key={child.id}>{row(child)}</div>)}
            </div> : null}
          </div>;
        })}
      </div>
    </div>
  );
}
